<script>
  import { onMount, setContext } from "svelte";
  import { createData } from "$utils/data.svelte.js";
  import { logClickToGA } from "@urbaninstitute/dataviz-components";

  // import bgImageUrl from '$assets/background-image.svg';
  import { getAbsoluteUrl } from "$lib/utils/urls.js";

  import LeftSidePage from "./LeftSidePage.svelte";
  import RightSidePage from "./RightSidePage.svelte";
  import GlossaryPopup from "./GlossaryPopup.svelte";

  import glossary_data from "$utils/glossary.aml";
  $inspect(glossary_data);

  const toolData = createData();

  // App-owned layer that chart tooltips portal into. It lives OUTSIDE the zoom:0.8 notebook
  // (so tooltip positioning isn't distorted by the zoom) but INSIDE this component's own DOM —
  // i.e. inside the shadow root when embedded as a <data-tool> custom element in a CMS. This
  // replaces the previous `.theme` querySelector lookup, which could resolve to null in the
  // embed and leave the tooltip unrendered. Exposed to descendant charts via context.
  let tooltipLayerEl = $state();
  setContext("tooltipLayer", () => tooltipLayerEl);

  const binderRings = Array.from({ length: 10 });
  const binderRingsVertical = Array.from({length: 7})

  const bgImageUrl = getAbsoluteUrl("background-image.svg");

  console.log(toolData);

  // Track every district focus change (search, chart click, dropdown, etc.) in GA.
  // Skips the initial default district so page load isn't counted.
  let prevDistrictID;
  $effect(() => {
    const id = toolData.activeDistrictID;
    if (!toolData.isReady()) return;
    if (prevDistrictID === undefined) {
      prevDistrictID = id; // establish baseline without logging page load
      return;
    }
    if (id === prevDistrictID) return;
    prevDistrictID = id;
    const name = toolData.activeDistrictData?.DistrictName ?? id;
    logClickToGA(id, "district_focus--" + name);
  });

  onMount(() => {
    toolData.fetchData();
  });
</script>

<!--This component controls the backgrounds and outermost design of the tool, 
  as well as the layout of the two pages at different widths and the notebook coils.
  It does not control content whatsoever, just layout.-->

  <div class="entire-background-with-icons" style="--bg-image: url('{bgImageUrl}')">
  <!-- App-owned tooltip layer: portal target that sits outside the zoomed notebook but inside
       this component (and thus inside the shadow root when embedded as a custom element). -->
  <div class="tooltip-layer" bind:this={tooltipLayerEl}></div>
  <div class="notebook-cover-dark-blue-background pages-container">
    <div class="left-side page">
      <LeftSidePage />
    </div>
    <div class="binder-rings" aria-hidden="true">
      {#each binderRings as _, index}
        <span class="binder-ring"></span>
      {/each}
    </div>
    <div class = "binder-rings-vertical" aria-hidden="true">
      {#each binderRingsVertical as _, index}
        <span class="binder-ring-vertical"> </span>
      {/each}
    </div>




    <div class="right-side page">
      <RightSidePage />
    </div>
    {#if toolData.isGlossaryOpen}
      <GlossaryPopup {glossary_data} onclose={() => (toolData.isGlossaryOpen = false)} />
    {/if}
  </div>
</div>

<style>
  /* ── Outer desk background — always full-width ───────────────────────────── */
  .entire-background-with-icons {
    padding: 60px 0;
    width: 100%;
    position: relative;
    isolation: isolate;
  }
  /* Zero-footprint overlay that chart tooltips are portaled into. It is a positioned,
     unpadded, unzoomed ancestor for the tooltip so page-coordinate positioning is exact.
     pointer-events: none so it never intercepts hover/click on the chart beneath it. */
  .tooltip-layer {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
    z-index: 1000;
  }
  .entire-background-with-icons::before {
    content: "";
    position: absolute;
    inset: 0;
    background-image: var(--bg-image);
    background-position: center;
    background-repeat: repeat;
    background-size: 1437px 1012px;
    opacity: 1;
    pointer-events: none;
    z-index: 0;
  }
  @media (max-width: 799px) {
    .entire-background-with-icons {
      padding: 20px 0;
    }
  }

  /* ── Notebook — fixed-width centered canvas per tier ─────────────────────── */
  .notebook-cover-dark-blue-background {
    /* mobile: fluid up to 375px */
    width: min(375px, 100%);
    margin-inline: auto;
    margin-block: 0;
    /* uniform blue border thickness across all tiers */
    padding: 24px;
    border-radius: 45px;
    position: relative;
    z-index: 1;
    background: var(--color-blue-shade-darker);
  }
  @media (min-width: 800px) {
    /* tablet: fixed 800px single-page canvas */
    .notebook-cover-dark-blue-background {
      width: 800px;
    }
  }
  @media (min-width: 1160px) {
    /* desktop: fixed 1500px two-page spread */
    .notebook-cover-dark-blue-background {
      width: 1500px;
    }
  }
  /* 1160–1499: show the full two-page desktop canvas, shrunk to 80% so the whole
     notebook (1500px → 1200px footprint) fits below full size. `zoom` reflows the
     layout box, so it scales layout, charts and type together without overflow. */
  @media (min-width: 1160px) and (max-width: 1499px) {
    .notebook-cover-dark-blue-background {
      zoom: 0.8;
    }
  }

  /* ── Pages grid — mobile-first single column, two-up only at desktop ─────── */
  .pages-container {
    display: grid;
    grid-template-columns: 1fr;
    gap: 0;
    position: relative;
  }
  @media (min-width: 1160px) {
    .pages-container {
      grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
      gap: 16px;
    }
  }

  /* ── Binder rings — horizontal (between stacked pages) below desktop ─────── */
  .binder-rings {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 30px;
    height: 100%;
    display: none;
    flex-direction: column;
    justify-content: space-evenly;
    align-items: center;
    pointer-events: none;
    z-index: 2;
  }
  @media (min-width: 1160px) {
    .binder-rings {
      display: flex;
    }
  }
  .binder-ring {
    position: relative;
    width: 65px;
    height: 11px;
    border-radius: 999px;
    background: var(--color-gray-shade-lighter);
  }
  .binder-ring::before,
  .binder-ring::after {
    content: "";
    position: absolute;
    top: 50%;
    width: 22px;
    height: 22px;
    border-radius: 100%;
    background: var(--color-blue-shade-darker);
    z-index: -1;
  }
  .binder-ring::before {
    left: 0;
    transform: translate(-35%, -50%);
  }
  .binder-ring::after {
    right: 0;
    transform: translate(35%, -50%);
  }

  .binder-rings-vertical {
    display: flex;
    flex-direction: row;
    justify-content: space-evenly;
    align-items: center;
    width: 100%;
    height: 16px;
    pointer-events: none;
    position: relative;
    z-index: 1;
  }
  @media (min-width: 1160px) {
    .binder-rings-vertical {
      display: none;
    }
  }
  .binder-ring-vertical {
    position: relative;
    width: 11px;
    height: 65px;
    border-radius: 999px;
    background: var(--color-gray-shade-lighter);
  }
  .binder-ring-vertical::before,
  .binder-ring-vertical::after {
    content: "";
    position: absolute;
    left: 50%;
    width: 22px;
    height: 22px;
    border-radius: 100%;
    background: var(--color-blue-shade-darker);
    z-index: -1;
  }
  .binder-ring-vertical::before {
    top: 0;
    transform: translate(-50%, -35%);
  }
  .binder-ring-vertical::after {
    bottom: 0;
    transform: translate(-50%, 35%);
  }

  /* ── Page cards ───────────────────────────────────────────────────────────── */
  .page {
    min-width: 0;
    padding: 24px 20px 16px;
    background-size: 20px 20px;
    background-color: var(--color-gray-shade-lightest);
    background-image:
      linear-gradient(to right, var(--color-gray-shade-lighter) 1px, transparent 1px),
      linear-gradient(to bottom, var(--color-gray-shade-lighter) 1px, transparent 1px);
    background-position: -1px -1px;
    border-radius: 20px;
  }
  /* stacked layout: left page gets bottom breathing room above the binder strip */
  .left-side.page {
    padding-bottom: 40px;
  }
  .right-side.page {
    padding-top: 40px;
  }
  @media (min-width: 800px) {
    .page {
      /* left padding 20px keeps content flush on the 20px background grid */
      padding: 30px 24px 16px 20px;
    }
    .left-side.page {
      padding-bottom: 40px;
    }
    .right-side.page {
      padding-top: 40px;
    }
  }
  @media (min-width: 1160px) {
    /* side-by-side: asymmetric inner padding faces the binder spine */
    .left-side.page {
      padding-left: 32px;
      padding-right: 64px;
      padding-bottom: 16px;
    }
    .right-side.page {
      padding-left: 60px;
      padding-right: 32px;
      padding-top: 30px;
    }
  }
</style>
